/**
 * Sign-in plumbing for the Plenum login page (docs/proposals/auth-native-login.md, option B).
 *
 * Cloudflare Access guards /api on every Plenum hostname and nothing else, so the SPA shell and
 * /login are public. Signing in means visiting one protected URL: Access intercepts it, emails the
 * one-time code, and afterwards returns the visitor to that URL, which the Worker turns into a hop
 * back to the page the visitor asked for. No Access hostname or audience is hardcoded here.
 */

/** Mirror of the Worker's rule (apps/api/src/routes/session.ts): same-origin paths only. */
export function safeNext(raw: unknown): string {
  if (typeof raw !== "string" || raw.length > 2048) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "/";
  // whitespace and control characters could smuggle a header into the redirect
  if (/\s/.test(raw) || Array.from(raw).some((ch) => ch.charCodeAt(0) < 32)) return "/";
  if (raw === "/login" || raw.startsWith("/login?") || raw.startsWith("/api/")) return "/";
  return raw;
}

/** The protected URL that starts the Access flow and lands the visitor on `next` afterwards. */
export function signInHref(next: string): string {
  return `/api/session/return?next=${encodeURIComponent(safeNext(next))}`;
}

/** Where to send a visitor who needs a session: our login page, remembering the page they wanted. */
export function loginPageHref(next: string): string {
  const safe = safeNext(next);
  return safe === "/" ? "/login" : `/login?next=${encodeURIComponent(safe)}`;
}

/** The page the browser is on, as a `next` value. */
export function currentPath(): string {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}
